/**
 * micro:bit 놀이터 3D 무대 템플릿 고르기(2026-10-08 · 4단계 — 장면 유형별 무대) — 순수 규칙. 장면 상태 → 무대 모습.
 *  - run      달리기 길: 운동장 트랙 · 표정 리모컨 코스 · 마라톤 · 두근두근 달리기 — 진행만큼 주인공이 길을 달린다 · 별 깃발 · 장애물
 *  - grid     격자 운동장: 운동장 보물찾기 — 주인공이 칸을 걷고, 보드가 받는 신호가 셀수록 전파 고리가 크게 퍼진다 · 찾으면 보물이 솟는다
 *  - plaza    라디오 광장: 친구 받침대를 반원으로 · 같은 그룹은 파란 고리 · 보내면 빛 알갱이가 같은 그룹 친구에게, 받으면 그 친구에게서 나에게
 *  - pedestal 그 밖 — 받침대 위 주인공(보드 조작을 따라 하는 몸짓)
 * 센서 월드 · 센서 기록 장면은 지금 구역에 맞춰 무대 밝기 · 빛 색(ambience)이 바뀐다 · 공연 장면(응원 · 음악 · 밴드)은 음마다 손님이 신난다.
 */
import type { SceneId } from "./scenes";
import { COURSE_LEN, OBSTACLES } from "./scenes";
import type { FaceCourse, Heartbeat, Track } from "./scenes";
import { MARATHON_LEN } from "./remotes";
import type { Lessons } from "./lessons";
import { FIELD_N, TREASURE, rssiOf } from "./stages";
import { SENSOR_SCENES } from "./sensorWorld";
import type { SensorState } from "./sensorWorld";
import type { Plaza } from "./radio";

export type MbTemplate =
  | { readonly kind: "pedestal" }
  | {
      readonly kind: "run";
      /** 길 모양(바뀌면 다시 만든다) */
      readonly marks: readonly number[];
      readonly obstacles: readonly number[];
      readonly progress: number;
      readonly air: boolean;
    }
  | {
      readonly kind: "grid";
      readonly n: number;
      readonly treasure: { readonly r: number; readonly c: number };
      readonly r: number;
      readonly c: number;
      /** 0~1 — 보드가 받는 신호(가까울수록 1) */
      readonly signal: number;
      readonly found: boolean;
    }
  | {
      readonly kind: "plaza";
      readonly members: readonly { readonly name: string; readonly same: boolean }[];
      /** 오간 것 수(늘면 새 전파) · 마지막 전파 */
      readonly n: number;
      readonly last: { readonly dir: "in" | "out"; readonly name: string | null } | null;
    };

export interface Ambience {
  readonly exposure: number;
  readonly tint: string | null;
}

export interface TemplateInput {
  readonly scene: SceneId;
  readonly lessons: Lessons;
  readonly track: Track;
  readonly course: FaceCourse;
  readonly heart: Heartbeat;
  readonly sensors: Readonly<Record<string, SensorState>>;
  /** 횟수 장면 별 기준 [⭐, ⭐⭐, ⭐⭐⭐](lib/sceneStars starTargets — 운영자 기준 포함) · 없으면 기본 */
  readonly targets?: readonly [number, number, number] | null;
  /** 라디오 광장(없으면 빈 광장) */
  readonly plaza?: Plaza;
  /** 광장 친구(나 빼고 · 별명) */
  readonly members?: readonly { readonly name: string }[];
}

/** 공연 장면 — 새 음마다 손님이 신난다 */
export const PERFORM_SCENES: readonly SceneId[] = ["cheer", "melody", "band", "quizShow"];

export function templateOf(x: TemplateInput): MbTemplate {
  switch (x.scene) {
    case "track": {
      // 길 끝 = 별 셋 기준 · 깃발 = 별 하나 · 둘 · 셋 자리
      const t = x.targets ?? [50, 80, 120];
      return {
        kind: "run",
        marks: t.map((v) => v / t[2]),
        obstacles: [],
        progress: Math.min(x.track.steps, t[2]) / t[2],
        air: false,
      };
    }
    case "faceCourse":
      return {
        kind: "run",
        marks: [1],
        obstacles: OBSTACLES.map((o) => o / COURSE_LEN),
        progress: x.course.pos / COURSE_LEN,
        air: x.course.air > 0,
      };
    case "marathon":
      return {
        kind: "run",
        marks: [1],
        obstacles: [],
        progress: x.lessons.remotes.marathon.pos / MARATHON_LEN,
        air: false,
      };
    case "heartbeat": {
      // 박동 별 하나 · 둘 · 셋 — 박동마다 한 칸
      const t = x.targets ?? [3, 6, 10];
      return {
        kind: "run",
        marks: t.map((v) => v / t[2]),
        obstacles: [],
        progress: Math.min(x.heart.beats, t[2]) / t[2],
        air: false,
      };
    }
    case "hotCold": {
      const s = x.lessons.stages.hotCold;
      // 신호(dBm −45 ~ −100) → 0~1
      const signal = Math.max(0, Math.min(1, (rssiOf(s) + 100) / 55));
      return {
        kind: "grid",
        n: FIELD_N,
        treasure: TREASURE,
        r: s.r,
        c: s.c,
        signal,
        found: s.found,
      };
    }
    case "plaza": {
      const p = x.plaza;
      const partners = p?.partners ?? [];
      const last = p?.log[p.log.length - 1];
      return {
        kind: "plaza",
        members: (x.members ?? []).map((m) => ({ name: m.name, same: partners.includes(m.name) })),
        n: p?.n ?? 0,
        last: last ? { dir: last.dir, name: last.name } : null,
      };
    }
    default:
      return { kind: "pedestal" };
  }
}

/** 템플릿 모양 열쇠 — 바뀌면 무대를 다시 만든다(진행 · 자리는 그대로 두고 갱신만) */
export function templateKey(t: MbTemplate): string {
  if (t.kind === "run") return `run:${t.marks.join(",")}:${t.obstacles.join(",")}`;
  if (t.kind === "grid") return `grid:${t.n}:${t.treasure.r},${t.treasure.c}`;
  if (t.kind === "plaza") return "plaza";
  return "pedestal";
}

/** 구역 이름 → 무대 밝기 · 빛 색(밤 · 동굴은 어둡게 · 해 질 녘은 붉게 · 냉장고 · 눈밭은 푸르게 · 모닥불 · 한낮은 따뜻하게) */
const ZONE_AMBIENCE: Readonly<Record<string, Ambience>> = {
  cave: { exposure: 0.4, tint: "#8FA6FF" },
  night: { exposure: 0.38, tint: "#8FA6FF" },
  sleep: { exposure: 0.42, tint: "#9AA8FF" },
  rest: { exposure: 0.45, tint: "#9AA8FF" },
  hotDark: { exposure: 0.45, tint: "#FFB070" },
  coldDark: { exposure: 0.42, tint: "#9CC8FF" },
  fridgeClosed: { exposure: 0.5, tint: "#9CC8FF" },
  dusk: { exposure: 0.72, tint: "#FFA36B" },
  sunset: { exposure: 0.72, tint: "#FF9A5C" },
  dawn: { exposure: 0.8, tint: "#FFC08A" },
  sunrise: { exposure: 0.85, tint: "#FFC08A" },
  day: { exposure: 1.15, tint: "#FFF1C4" },
  sunny: { exposure: 1.2, tint: "#FFF1C4" },
  noon: { exposure: 1.2, tint: "#FFF7D6" },
  play: { exposure: 1.15, tint: "#FFF1C4" },
  hotBright: { exposure: 1.2, tint: "#FFD9A0" },
  fridgeOpen: { exposure: 1.0, tint: "#D6EEFF" },
  bonfire: { exposure: 0.85, tint: "#FFA352" },
  snowfield: { exposure: 1.05, tint: "#D6ECFF" },
  noisy: { exposure: 1.1, tint: "#FFD0D0" },
  tense: { exposure: 1.05, tint: "#FFB8B8" },
  quiet: { exposure: 0.85, tint: "#D8E4FF" },
  calm: { exposure: 0.9, tint: "#E0ECFF" },
};

/** 센서 장면의 지금 구역(아직 할 구역 · 다 했으면 마지막 구역)에 맞는 무대 밝기 — 센서 장면이 아니거나 모르는 구역이면 null(기본) */
export function ambienceOf(
  scene: SceneId,
  sensors: Readonly<Record<string, SensorState>>,
): Ambience | null {
  const sc = SENSOR_SCENES.find((s) => s.id === scene);
  if (!sc || !sc.zones.length) return null;
  const st = sensors[scene];
  const i = Math.min(st?.zone ?? 0, sc.zones.length - 1);
  return ZONE_AMBIENCE[sc.zones[i]!.key] ?? null;
}
